const uniqueDocumentKeys = (keys) => Array.from(new Set(
    (Array.isArray(keys) ? keys : [])
        .filter(key => typeof key === 'string')
        .filter(Boolean)
));

const getDocumentStorageScope = (key) => {
    const match = String(key || '').match(/^(cases|leads)\/([^/]+)\//i);
    return match ? `${match[1].toLowerCase()}/${match[2]}` : '';
};

const collapseNestedDocumentSelection = (keys) => {
    const candidates = uniqueDocumentKeys(keys);
    const selectedFolders = candidates.filter(key => key.endsWith('/'));

    return candidates.filter(key => !selectedFolders.some(folderKey => (
        folderKey !== key && key.startsWith(folderKey)
    )));
};

export const resolveDocumentSelectionGesture = ({
    selectedKeys,
    visibleKeys,
    itemKey,
    anchorKey,
    shiftKey = false,
    toggleKey = false,
    forceToggle = false,
}) => {
    const normalizedItemKey = String(itemKey || '');
    if (!normalizedItemKey || (!forceToggle && !shiftKey && !toggleKey)) {
        return {
            handled: false,
            selectedKeys: uniqueDocumentKeys(selectedKeys),
            anchorKey: anchorKey || null,
        };
    }

    const currentSelection = uniqueDocumentKeys(selectedKeys);
    const orderedVisibleKeys = uniqueDocumentKeys(visibleKeys);
    const referencedAnchor = String(anchorKey || '');
    const resolvedAnchor = (
        referencedAnchor
        && currentSelection.includes(referencedAnchor)
        && orderedVisibleKeys.includes(referencedAnchor)
    )
        ? referencedAnchor
        : ([...currentSelection].reverse().find(key => orderedVisibleKeys.includes(key)) || '');

    let nextSelection;
    if (shiftKey) {
        const anchorIndex = orderedVisibleKeys.indexOf(resolvedAnchor);
        const targetIndex = orderedVisibleKeys.indexOf(normalizedItemKey);
        const rangeKeys = resolvedAnchor && anchorIndex >= 0 && targetIndex >= 0
            ? orderedVisibleKeys.slice(
                Math.min(anchorIndex, targetIndex),
                Math.max(anchorIndex, targetIndex) + 1
            )
            : [normalizedItemKey];
        nextSelection = uniqueDocumentKeys([...currentSelection, ...rangeKeys]);
    } else {
        nextSelection = currentSelection.includes(normalizedItemKey)
            ? currentSelection.filter(key => key !== normalizedItemKey)
            : [...currentSelection, normalizedItemKey];
    }

    return {
        handled: true,
        selectedKeys: nextSelection,
        anchorKey: shiftKey ? (resolvedAnchor || normalizedItemKey) : normalizedItemKey,
    };
};

export const buildDocumentDragPayload = ({ type, item, sourceFolder, selectedKeys }) => {
    const draggedKey = item?.key || item?.file_path || item?.path || '';
    const selection = uniqueDocumentKeys(selectedKeys);
    const draggedScope = getDocumentStorageScope(draggedKey);
    const sourceKeys = selection.includes(draggedKey)
        ? collapseNestedDocumentSelection(
            selection.filter(key => draggedScope && getDocumentStorageScope(key) === draggedScope)
        )
        : [draggedKey];

    return {
        type,
        item,
        sourceFolder,
        ...(sourceKeys.length > 1 ? {
            documentDragVersion: 2,
            sourceKeys,
        } : {}),
    };
};

export const resolveDocumentDropSourceKeys = (dragData) => {
    const draggedKey = dragData?.item?.key || dragData?.item?.file_path || dragData?.item?.path || '';
    if (!draggedKey) return [];

    const requestedKeys = dragData?.documentDragVersion === 2 && Array.isArray(dragData?.sourceKeys)
        ? uniqueDocumentKeys(dragData.sourceKeys)
        : [];
    const draggedKeyCovered = requestedKeys.some(key => (
        key === draggedKey || (key.endsWith('/') && draggedKey.startsWith(key))
    ));
    if (!draggedKeyCovered) return [draggedKey];

    const draggedScope = getDocumentStorageScope(draggedKey);
    if (!draggedScope) return [draggedKey];

    return collapseNestedDocumentSelection(
        requestedKeys.filter(key => getDocumentStorageScope(key) === draggedScope)
    );
};
